<script lang="ts" setup>
import { useToast } from 'vue-toastification';
import ToastComponent from '~/components/ui/ToastComponent.vue';

const props = defineProps<{ questionId: number }>();
const emits = defineEmits(['close-modal', 'shrink-form', 'submit-reply']);

const feedbackStore = useFeedbackStore();
const { userDetails } = storeToRefs(useProfileStore());

const initialReplyFormState = {
  message: '',
  person_name: userDetails.value ? `${userDetails.value.first_name} ${userDetails.value.second_name.charAt(0)}.` : '',
  images: [] as File[],
};

const replyForm = ref(structuredClone(initialReplyFormState));
const errors = ref({ name: '', message: '' });
const isLoading = ref(false);
const previews = ref<string[]>([]);

const messageMinLength = ref(10);
const messageMaxLength = ref(3000);
const nameMinLength = ref(2);

watch(
  () => replyForm.value,
  () => {
    if (replyForm.value.person_name && replyForm.value.person_name.length < nameMinLength.value) errors.value.name = '';
    if (
      replyForm.value.message &&
      replyForm.value.message.length >= messageMinLength.value &&
      replyForm.value.message.length <= messageMaxLength.value
    )
      errors.value.message = '';
  },
  { deep: true },
);

const hasErrors = computed(() => !!errors.value.name || !!errors.value.message);

const yandexCaptcha = ref();
const captchaToken = ref('');

watch(
  () => captchaToken.value,
  () => {
    if (captchaToken.value) submitReply();
  },
);

async function submitReply() {
  if (!replyForm.value.person_name) errors.value.name = 'Введите имя';
  if (replyForm.value.person_name.length < nameMinLength.value)
    errors.value.name = `Минимум ${nameMinLength.value} символов`;

  if (!replyForm.value.message) errors.value.message = 'Поле обязательно для заполнения';
  if (replyForm.value.message.length < messageMinLength.value)
    errors.value.message = `Минимум ${messageMinLength.value} символов`;

  if (hasErrors.value) return;

  isLoading.value = true;

  if (captchaToken.value) {
    const response = await feedbackStore.sendQuestionReply(props.questionId, replyForm.value, captchaToken.value);

    if (response?.success) {
      const toast = useToast();
      toast.success({
        component: ToastComponent,
        props: { text: 'Спасибо! Ваш ответ отправлен.' },
        timeout: 4000,
      });

      replyForm.value = structuredClone(initialReplyFormState);
      previews.value = [];

      const q = feedbackStore.questions.find((q) => q.id === response.data.question_id);

      if (q) {
        const newResponse = {
          id: response.data.id,
          message: response.data.body,
          replier: {
            id: response.data.replier_id,
            name: response.data.person_name,
            avatar: null,
          },
          images: [],
          likers: [],
          dislikers: [],
          created_at: response.data.created_at,
        };

        q.replies.push(newResponse);
      }
      emits('submit-reply');
    }
    yandexCaptcha.value?.reset();
  } else {
    yandexCaptcha.value?.execute();
  }

  isLoading.value = false;
}
</script>

<template>
  <div class="add-reply-form">
    <div class="flex flex-col gap-3">
      <TextareaField
        v-model="replyForm.message"
        :error="errors.message"
        :max-len="messageMaxLength"
        :min-len="messageMinLength"
        autofocus
        name="message"
        placeholder="Ваш ответ на вопрос"
      />

      <InputField
        v-model="replyForm.person_name"
        :error="errors.name"
        :min-len="nameMinLength"
        name="person name"
        placeholder="Ваше имя"
      />

      <ProductPageFeedbackImagesUploadBlock
        v-model:images="replyForm.images"
        v-model:previews="previews"
      />
    </div>

    <div class="add-reply-form__btns">
      <TheBtn
        class="!hidden sm:!flex"
        color="outline"
        size="sm"
        @click="emits('shrink-form')"
      >
        Отменить
      </TheBtn>

      <TheBtn
        :disabled="replyForm.message.length === 0 || replyForm.person_name.length === 0 || hasErrors"
        :is-loading="isLoading"
        class="w-full sm:w-fit"
        size="sm"
        @click="submitReply"
      >
        Отправить
      </TheBtn>
    </div>

    <YandexCaptcha
      ref="yandexCaptcha"
      v-model="captchaToken"
    />
  </div>
</template>

<style lang="scss" scoped>
.add-reply-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 580px;

  &__btns {
    display: flex;
    flex: auto 1 1;
    gap: 8px;
    width: 100%;

    @include screen-sm {
      justify-items: flex-end;
    }
  }
}
</style>
